import { map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { Language } from '../../classes/Language';
import { reloadPage, iterableToArray } from '../../helpers/helpers';
import { getStorageItem, setStorageItem, removeStorageItem } from '../../helpers/starterHelpers';
import { AsperaError } from '../../classes/AsperaError';
import { Axios } from '../../classes/Axios';
import React from 'react';
import { defaultEnglish, storageKeys } from '../../constants/constants';
import globals from '../../constants/en-us/globals';
/**
 * Get translations and formatted data for the current language used in the session
 */
export class LanguageService {
    constructor() {
        /** Default language of the application and used for fallback */
        this.fallbackLanguage = 'en-us';
        /** Key used for storing settings in browser storage */
        this.saveKey = storageKeys.language;
        /** Key used for storage setting for language testing */
        this.testKey = storageKeys.languageTest;
        /** Current language being used in the application */
        // eslint-disable-next-line
        this.currentLanguage = new Language(this.fallbackLanguage, new Map([defaultEnglish]));
        /** Current dictionary being used in the session */
        // eslint-disable-next-line
        this.dictionary = {};
        /** English US dictionary used if language not available as well as for default */
        // eslint-disable-next-line
        this.englishUsDictionary = globals;
        /** List of available languages */
        this.availableLanguages = new Map();
        window.toggleLanguageTest = () => {
            if (getStorageItem(this.testKey)) {
                removeStorageItem(this.testKey);
            }
            else {
                setStorageItem(this.testKey, 'true');
            }
        };
    }
    /**
     *
     * @param langCode - language code to use for current language
     */
    setCurrentLanguage(langCode) {
        this.currentLanguage = new Language(this.validateLanguageString(langCode), this.availableLanguages);
        const body = document.querySelector('body');
        if (this.currentLanguage.rightToLeft) {
            body === null || body === void 0 ? void 0 : body.classList.add('language-right-left');
        }
        else {
            body === null || body === void 0 ? void 0 : body.classList.remove('language-right-left');
        }
    }
    /**
     * Safely check if the keys exist (all language items are nested 2 levels down)
     *
     * @param key - language code that references the translation files
     *
     * @returns the found item
     */
    getResultSafely(key, englishOnly) {
        const keyParts = key.split('.');
        if (!englishOnly && this.dictionary[keyParts[0]] && this.dictionary[keyParts[0]][keyParts[1]]) {
            return this.dictionary[keyParts[0]][keyParts[1]];
        }
        else if (this.englishUsDictionary[keyParts[0]] && this.englishUsDictionary[keyParts[0]][keyParts[1]]) {
            return this.englishUsDictionary[keyParts[0]][keyParts[1]];
        }
        return '';
    }
    /**
     * Finds the closest language to use. For example if FR-CA is requested, FR-FR will be used
     *
     * @param lang - test string to find closest language
     *
     * @returns closest language to use or en-us
     */
    findClosestLanguage(lang) {
        if (this.availableLanguages.get(lang)) {
            return lang;
        }
        else {
            const languageCode = lang.split('-')[0];
            const closeLanguages = iterableToArray(this.availableLanguages.keys()).filter(key => {
                return key.indexOf(`${languageCode}-`) > -1;
            });
            return closeLanguages[0] || this.fallbackLanguage;
        }
    }
    /**
     * Get storage language from browser session
     *
     * @returns the stored language if one is set
     */
    getStoredLanguage() {
        let language = getStorageItem(this.saveKey);
        if (language) {
            language = language.toLowerCase();
            if (this.availableLanguages.get(this.validateLanguageString(language))) {
                return language;
            }
            else {
                new AsperaError('LanguageService: Language stored in session is invalid');
            }
        }
    }
    /**
     * Set the dictionary after retrieving it or use English
     *
     * @param lang - language key to use for dictionary retrieval
     *
     * @returns an observable when it finishes setting up the language
     */
    setDictionary(lang) {
        const foundLanguage = this.availableLanguages.get(this.validateLanguageString(lang));
        if (!foundLanguage || !(foundLanguage.location || foundLanguage.data)) {
            this.dictionary = this.englishUsDictionary;
            return of(true);
        }
        else {
            this.setCurrentLanguage(lang);
            if (foundLanguage.data) {
                this.dictionary = foundLanguage.data;
                return of(true);
            }
            return Axios.get(`${foundLanguage.location}`).pipe(map(response => {
                this.dictionary = response.data;
                return true;
            }), catchError(error => {
                new AsperaError('LanguageService: Could not retrieve requested language', { lang, error }, true);
                this.dictionary = this.englishUsDictionary;
                this.setCurrentLanguage(this.fallbackLanguage);
                return of(true);
            }));
        }
    }
    /**
     * Get the browser language (navigator)
     *
     * @returns the browser language
     */
    getBrowserLanguage() {
        if (typeof navigator.language === 'string') {
            return navigator.language.toLowerCase();
        }
        else {
            new AsperaError('LanguageService: Browser language could not be detected');
            return this.fallbackLanguage;
        }
    }
    /**
     * Returns the language key that is allowed by the availableLanguages model
     *
     * @param testString - string to validate against proper list of languages supported
     *
     * @returns language code that is valid (defaults to en-us)
     */
    validateLanguageString(testString) {
        const validLanguages = iterableToArray(this.availableLanguages.keys());
        return validLanguages.indexOf(testString) > -1 ? testString : this.fallbackLanguage;
    }
    /**
     * Get an override language based on key
     *
     * @param overrideLanguage - the language key to override if not set will return currentLanguage
     */
    getOverrideLanguageOrDefault(overrideLanguage) {
        return overrideLanguage ? new Language(overrideLanguage, this.availableLanguages) : this.currentLanguage;
    }
    /**
     * Set initial language data prior to activation
     *
     * @param englishUs - the full english US dictionary, this will be merged with globals
     * @param availableLanguages - the available languages for the system
     *
     * @returns an observable when it finishes setting up the language
     */
    // eslint-disable-next-line
    initDictionary(englishUs, availableLanguages) {
        this.englishUsDictionary = Object.assign(englishUs, globals);
        this.availableLanguages = availableLanguages;
    }
    /**
     * Utilize a language based or fallback to closest one available.
     *
     * @param langCode - Language code to use
     *
     * @returns an observable when it finishes setting up the language
     */
    utilizeLanguage(langCode = '') {
        const languageToUse = (this.findClosestLanguage(this.getStoredLanguage() || langCode.toLowerCase() || this.getBrowserLanguage() || this.fallbackLanguage));
        this.setCurrentLanguage(languageToUse);
        return this.setDictionary(languageToUse);
    }
    /**
     * Get the requested language translation (all language items are nested 2 levels down)
     *
     * @param key - language code that references the translation files
     * @param params - array of items that can be replaced in the translation (based on $0, $1, $2...)
     * @param englishOnly - indicates that only English dictionary should be used (mostly for tracking service)
     *
     * @returns the found item
     */
    get(key, params, englishOnly) {
        if (getStorageItem(this.testKey)) {
            return `${key}${Array.isArray(params) ? ` (${params.toString()})` : ''}`;
        }
        if (!this.currentLanguage) {
            new AsperaError('LanguageService: Language service has not been loaded prior to text request');
            return key;
        }
        let result = this.getResultSafely(key, englishOnly);
        if (!result) {
            new AsperaError('LanguageService: Requested language key not found in current language or English US', { key, language: this.currentLanguage }, true);
            return `**${key}**`;
        }
        if (Array.isArray(params)) {
            params.forEach((element, index) => {
                result = result.replace(`$${index}`, String(element));
            });
        }
        return result;
    }
    /**
     * Get the requested string with fancy styling. This will return a SPAN tag with the data inside.
     *
     * Language keys: "My new string is in $i/italics/$i and this is in $b/bold/$b and this is in $m/mono type font/$m and finally this is a $l/link/$l"
     * Guide: $i - italics, $b - bold, $m - mono type font, $l - link (anchor)
     *
     * @param key - language code that references the translation files
     * @param params - array of items that can be replaced in the translation (based on $0, $1, $2...)
     * @param links - this will set a href (if string).  Use as an Array to support multiple links
     */
    getStyled(key, params, links) {
        if (getStorageItem(this.testKey)) {
            return `${key}${Array.isArray(params) ? ` (${params.toString()})` : ''}`;
        }
        let stringValue = this.get(key, params);
        stringValue = stringValue.replace(/\$i\//g, '<em>');
        stringValue = stringValue.replace(/\/\$i/g, '</em>');
        stringValue = stringValue.replace(/\$b\//g, '<strong>');
        stringValue = stringValue.replace(/\/\$b/g, '</strong>');
        stringValue = stringValue.replace(/\$m\//g, '<code>');
        stringValue = stringValue.replace(/\/\$m/g, '</code>');
        stringValue = stringValue.replace(/\$i\//g, '<em>');
        stringValue = stringValue.replace(/\/\$i/g, '</em>');
        if (Array.isArray(links)) {
            links.forEach(link => {
                if (typeof link === 'string') {
                    stringValue = stringValue.replace('$l/', `<a href="${link}" class="cds--link" target="_blank">`);
                }
                stringValue = stringValue.replace('/$l', '</a>');
            });
        }
        stringValue = stringValue.replace(/\$l\//g, '');
        stringValue = stringValue.replace(/\/\$l/g, '');
        return React.createElement('span', { dangerouslySetInnerHTML: { __html: stringValue }, className: 'aspera-style-translate' });
    }
    /**
     * Get available languages to show in a dropdown format
     *
     * @param includeHidden - indicate if hidden languages should be returned
     *
     * @returns language list with objects containing name (to display) and key for setting in LanguageService
     */
    getAvailableLanguages(includeHidden) {
        const languageOptions = [];
        this.availableLanguages.forEach(value => {
            if (!value.hidden || includeHidden) {
                languageOptions.push({
                    name: value.name,
                    code: value.code,
                });
            }
        });
        return languageOptions;
    }
    /**
     * Gets the current language in use for the session
     *
     * @returns the language object
     */
    getCurrentLanguage() {
        return this.currentLanguage;
    }
    /**
     * Change the language used in the session
     *
     * @param lang - language code to use in session
     */
    changeLanguage(lang) {
        if (!lang || typeof lang !== 'string') {
            removeStorageItem(this.saveKey);
            reloadPage();
            return;
        }
        lang = lang.toLowerCase();
        if (!this.availableLanguages.get(lang)) {
            new AsperaError('LanguageService: Invalid language used for change language', { lang }, true);
            return;
        }
        setStorageItem(this.saveKey, lang);
        reloadPage();
    }
    /**
     * Get the international date format for the current language
     *
     * @param date - item that can be used as a JavaScript date object
     * @param type - formatted date type for quick formats
     * @param custom - custom formats (see JavaScript DateTimeFormatOptions)
     * @param overrideLanguage - string representing language to use (should not be used in normal flows)
     *
     * @returns formatted date string for displaying
     */
    getDate(date, type = 'fullDate', custom, overrideLanguage) {
        if (!date) {
            return '\u2014';
        }
        const types = {
            fullDate: { month: 'long', year: 'numeric', day: 'numeric', weekday: 'long' },
            longDate: { month: 'long', year: 'numeric', day: 'numeric' },
            monthYear: { month: 'long', year: 'numeric' },
            mediumDate: { month: 'short', year: 'numeric', day: 'numeric' },
            mediumMonthDay: { month: 'short', day: 'numeric' },
            mediumMonthDayTime: { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric' },
            shortDate: { month: 'numeric', year: '2-digit', day: 'numeric' },
            short: { month: 'numeric', year: '2-digit', day: 'numeric', hour: 'numeric', minute: 'numeric' },
            shortMonthDay: { month: 'numeric', day: 'numeric' },
            medium: { month: 'short', year: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' },
            mediumTime: { hour: 'numeric', minute: 'numeric', second: 'numeric' },
            shortTime: { hour: 'numeric', minute: 'numeric' },
        };
        const dateObject = new Date(date);
        if (isNaN(dateObject.getTime())) {
            return String(date);
        }
        const languageToUse = this.getOverrideLanguageOrDefault(overrideLanguage);
        return dateObject.toLocaleString(languageToUse ? (languageToUse.timeFormat || languageToUse.code) : undefined, custom ? custom : types[type]);
    }
    /** Indicate if left to right styling is in use */
    get leftRightStyle() {
        return !!this.currentLanguage.rightToLeft;
    }
    /**
     * Get the international bytes format for the current language
     *
     * @param value - item to return as byte or bit format
     * @param decimalPoints - how many decimal points to round to
     * @param base - base to use for numbers (default: 1000 when using bits per second otherwise 1024)
     * @param bytesPerSecond - use bytes per second format
     * @param bitsPerSecond - use bits per second format
     */
    getBytes(value, decimalPoints = 0, base, bytesPerSecond, bitsPerSecond) {
        if (value === null || value === '' || Array.isArray(value) || isNaN(Number(value))) {
            return '\u2014';
        }
        if (!base) {
            base = bitsPerSecond ? 1000 : 1024;
        }
        const bytesOptions = [
            this.get('bytes.byteAbb'),
            this.get('bytes.byteKiloAbb'),
            this.get('bytes.byteMegaAbb'),
            this.get('bytes.byteGigaAbb'),
            this.get('bytes.byteTeraAbb'),
            this.get('bytes.bytePetaAbb'),
            this.get('bytes.byteExaAbb'),
        ];
        const bytesPerSecondOptions = [
            this.get('bytes.bytePsAbb'),
            this.get('bytes.byteKiloPsAbb'),
            this.get('bytes.byteMegaPsAbb'),
            this.get('bytes.byteGigaPsAbb'),
            this.get('bytes.byteTeraPsAbb'),
            this.get('bytes.bytePetaPsAbb'),
            this.get('bytes.byteExaPsAbb'),
        ];
        const bitsPerSecondOptions = [
            this.get('bytes.bitPsAbb'),
            this.get('bytes.bitKiloPsAbb'),
            this.get('bytes.bitMegaPsAbb'),
            this.get('bytes.bitGigaPsAbb'),
            this.get('bytes.bitTeraPsAbb'),
            this.get('bytes.bitPetaPsAbb'),
            this.get('bytes.bitExaPsAbb'),
        ];
        let unitIndex = 0;
        let units = [];
        let valueToUse = Number(value);
        let valueToReturn = '';
        if (bytesPerSecond) {
            units = bytesPerSecondOptions;
        }
        else if (bitsPerSecond) {
            units = bitsPerSecondOptions;
        }
        else {
            units = bytesOptions;
        }
        if (Math.abs(valueToUse) > base) {
            do {
                valueToUse /= base;
                ++unitIndex;
            } while (Math.abs(valueToUse) >= base && unitIndex < units.length - 1);
        }
        try {
            valueToReturn = Number(valueToUse.toFixed(decimalPoints)).toLocaleString(this.currentLanguage.code, { minimumFractionDigits: decimalPoints });
        }
        catch (_a) {
            valueToReturn = valueToUse.toFixed(decimalPoints);
        }
        return `${valueToReturn} ${units[unitIndex]}`;
    }
    /**
     * Get the international currency format for the current language
     *
     * @param currency - the currency to use
     * @param value - the value to format
     * @param overrideLanguage - string representing language to use (should not be used in normal flows)
     */
    getCurrencyFormat(value, currency = 'USD', style = 'currency', overrideLanguage) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return Intl.NumberFormat(overrideLanguage || this.getCurrentLanguage().code, { style: style, currency }).format(value);
    }
    /**
     * Get the international number format for the current language
     *
     * @param num - number to format
     * @param decimalPoints - number of decimal points
     * @param overrideLanguage - string representing language to use (should not be used in normal flows)
     */
    getNumberFormat(num, decimalPoints = 0, overrideLanguage) {
        return Number(num).toLocaleString(overrideLanguage || this.getCurrentLanguage().code, { maximumFractionDigits: decimalPoints });
    }
}
/** The shared language service */
export const language = new LanguageService();
